import { AccountField, CompanyField } from 'components/shared/dropdowns'
import {
  Box,
  FlagField,
  FormModal,
  SpinnerButton,
  TextField,
  ValueAutocompleteField,
  useRouter,
} from '@forgeglobal/design-framework'
import {
  ClientContextDocument,
  CreateOrUpdateOpportunityMutationVariables,
  ListingStructureType,
  MinimalOpportunitiesDocument,
  OpportunityAccountDocument,
  OpportunityCertainty,
  OpportunityCompanyFinancingDocument,
  OpportunityDetailsDocument,
  OpportunityHeaderDocument,
  TradeIntentType,
  useAccountNameQuery,
  useCompanyNameQuery,
  useCreateOrUpdateOpportunityMutation,
  useIssuerQuery,
  useOpportunityQuery,
} from 'graphql/bindings/AdminBindings'
import { DEFAULT_FEE } from 'util/PricingUtils'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { PercentField } from 'components/shared/PercentField'
import { Routes } from 'util/Routes'
import { bidOrAskStructures } from 'util/Enums'
import { createRandomString } from 'util/StringUtils'
import { formatPercent, parsePercent } from 'util/NumberUtils'
import { formatPriceOrValuation, formatSharesOrSize } from 'util/ParsersFormatters'
import { parseSizeOrPrice, validateFeeField, validatePriceOrValuation, validateSizeOrSharesField } from 'util/FormUtils'
import React, { useState } from 'react'
import compact from 'lodash/compact'
import omit from 'lodash/omit'
import startCase from 'lodash/startCase'

type FormShape = {
  id?: string
  account_id?: { label: string; value: string }
  company_id?: { label: string; value: number }
  type?: TradeIntentType
  structure?: Array<ListingStructureType>
  latest_activity?: string
  create_and_add_new: boolean
  is_new: boolean
  postToSlackChannel?: 'marketplace' | 'client_strategy' | 'marketplace_team'
  mutationResult: MutationResult
  size_or_shares?: string
  price_or_valuation?: string
  fee?: string
}

const OpportunityForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, values, initialValues, valid }) => {
  const mutateInProgress = values.mutationResult.loading
  return (
    <form onSubmit={handleSubmit}>
      <AccountField name="account_id" label="Account (required)" />

      <CompanyField name="company_id" label="Issuer (required)" />

      <ValueAutocompleteField
        name="type"
        label="Opportunity Type (required)"
        options={[
          { label: 'Bid', value: TradeIntentType.Bid },
          { label: 'Ask', value: TradeIntentType.Ask },
        ]}
      />

      <Field
        component={TextField}
        name="size_or_shares"
        label="Size/Shares"
        format={formatSharesOrSize}
        formatOnBlur
        fullWidth
      />

      <Field
        component={TextField}
        name="price_or_valuation"
        label="Price/Valuation"
        format={formatPriceOrValuation}
        formatOnBlur
        fullWidth
      />

      <PercentField name="fee" label="Fee" />

      <ValueAutocompleteField
        name="structure"
        options={values.type ? bidOrAskStructures(values.type) : []}
        label="Structure"
      />

      <Field name="latest_activity" component={TextField} label="Additional information" fullWidth multiline />

      <ValueAutocompleteField
        name="postToSlackChannel"
        label="Share to Slack Channel"
        options={[
          { value: 'marketplace', label: '#marketplace' },
          { value: 'client_strategy', label: '#client_strategy' },
          { value: 'marketplace_team', label: '#marketplace-team' },
        ]}
      />

      {initialValues.is_new && (
        <Field name="create_and_add_new" component={FlagField} label="Create and add new" fullWidth />
      )}

      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!valid}
          fullWidth
          loading={mutateInProgress}
          caption={values.is_new ? 'Create' : 'Save'}
        />
      </Box>
    </form>
  )
}

export const OpportunityModal: React.FC<{
  hideModal: () => void
  opportunityId?: string
  accountId?: string
  issuerId?: string
}> = ({ hideModal, opportunityId, accountId, issuerId }) => {
  const { data: opportunityData } = useOpportunityQuery({ variables: { id: opportunityId }, skip: !opportunityId })
  const opportunity = opportunityData?.opportunity
  const { history } = useRouter()
  const [key, setKey] = useState(createRandomString())
  const { data: issuerData } = useIssuerQuery({ variables: { id: issuerId }, skip: !issuerId })
  accountId = opportunity?.account_id ?? accountId
  const companyId = opportunity?.company?.id ?? issuerData?.account?.issuer_account?.company_id
  const certainty: OpportunityCertainty = opportunity?.certainty ?? OpportunityCertainty.Soft
  const accountDetails = useAccountNameQuery({
    variables: { id: accountId },
    skip: !accountId,
  })
  const account = accountDetails?.data?.account

  const companyDetails = useCompanyNameQuery({
    variables: { companyId: companyId! },
    skip: !companyId,
  })
  const company = companyDetails?.data?.company

  const [mutate, mutateResult] = useCreateOrUpdateOpportunityMutation({
    refetchQueries: compact([
      (accountId || issuerId) && {
        query: MinimalOpportunitiesDocument,
        variables: { accountId: accountId || issuerId },
      },
      opportunityId && { query: OpportunityHeaderDocument, variables: { opportunityId } },
      opportunityId && { query: ClientContextDocument, variables: { opportunityId } },
      opportunityId && { query: OpportunityAccountDocument, variables: { opportunityId } },
      opportunityId && { query: OpportunityCompanyFinancingDocument, variables: { opportunityId } },
      opportunityId && { query: OpportunityDetailsDocument, variables: { opportunityId } },
    ]),
  })

  const opportunityFee = formatPercent(opportunity?.trade_intent?.fee ?? DEFAULT_FEE)

  const initialValues: FormShape = {
    id: opportunity?.id,
    account_id: account && { label: account.name, value: account.id },
    company_id: company && { label: company.name, value: company.id },
    type: opportunity?.type,
    structure: opportunity?.trade_intent?.acceptable_structures,
    latest_activity: opportunity?.latest_activity ?? undefined,
    create_and_add_new: false,
    is_new: !opportunityId,
    postToSlackChannel: 'marketplace',
    mutationResult: mutateResult,
    fee: opportunityFee,
  }

  const validate = (values: FormShape) => {
    const { size_or_shares, price_or_valuation, type, fee } = values
    const requiredFields = ['account_id', 'company_id', 'type'] as string[]
    const valueObj = values as { [key: string]: any }
    const errors = {
      size_or_shares: validateSizeOrSharesField(size_or_shares),
      price_or_valuation: validatePriceOrValuation(price_or_valuation, type),
      fee: validateFeeField(fee),
    }

    return requiredFields.reduce((acc, fieldName) => {
      if (!valueObj[fieldName]) {
        const missingField = fieldName === 'company_id' ? 'Issuer' : startCase(fieldName)
        return { ...acc, [fieldName]: `${missingField} is required` }
      }

      return acc
    }, errors)
  }

  const onSubmit = (values: FormShape) => {
    const fee = values?.fee !== undefined ? parsePercent(values.fee) : DEFAULT_FEE

    const parsedSizedValues = values.size_or_shares ? parseSizeOrPrice(values.size_or_shares, 'size', fee) : {}

    const parsedPriceValues = values.price_or_valuation ? parseSizeOrPrice(values.price_or_valuation, 'price', fee) : {}

    const variables: CreateOrUpdateOpportunityMutationVariables = {
      form: {
        ...(omit(values, [
          'create_and_add_new',
          'is_new',
          'mutationResult',
          'size_or_shares',
          'price_or_valuation',
        ]) as any),
        fee,
        ...parsedSizedValues,
        ...parsedPriceValues,
        account_id: values.account_id!.value,
        company_id: values.company_id!.value,
        renew: true,
        certainty: certainty,
      },
    }

    mutate({ variables }).then(response => {
      const { create_and_add_new, is_new } = values
      if (create_and_add_new) {
        setKey(createRandomString())
      } else {
        hideModal()
        const responseOpportunityId = response?.data?.createOrUpdateOpportunity?.id
        if (is_new && responseOpportunityId) {
          history.push(Routes.opportunity(responseOpportunityId))
        }
      }
    })
  }

  return (
    <FormModal open hideModal={hideModal} title={`${opportunityId ? 'Edit' : 'Create'}: Opportunity`}>
      <div key={key}>
        <Form<FormShape>
          component={OpportunityForm}
          onSubmit={onSubmit}
          validate={validate}
          initialValues={{ ...initialValues }}
        />
      </div>
    </FormModal>
  )
}
